import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, TrendingUp, TrendingDown, Clock, AlertCircle, CheckCircle, Sparkles } from 'lucide-react'
import { useStockSearch } from '../../hooks/useStockSearch'
import { useTradingSimulation } from '../../hooks/useTradingSimulation'
import { useAuth } from '../../hooks/useAuth'
import SignInPrompt from '../SignInPrompt'

interface TradingPanelProps {
  onOrderExecuted?: () => void
}

const TradingPanel: React.FC<TradingPanelProps> = ({ onOrderExecuted }) => {
  const { user, isGuest } = useAuth()
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)
  const {
    searchQuery,
    searchResults,
    selectedStock,
    setSearchQuery,
    selectStock,
    clearSearch
  } = useStockSearch()

  const {
    tradingState,
    loading,
    error,
    submitting,
    setOrderType,
    setQuantity,
    setSelectedStock,
    calculateOrderPreview,
    executeOrder,
    resetTradingState,
    marketHours
  } = useTradingSimulation()

  // Update selected stock in trading state when stock is selected
  useEffect(() => {
    if (selectedStock) {
      setSelectedStock(selectedStock)
    }
  }, [selectedStock, setSelectedStock])

  // Calculate order preview when relevant data changes
  useEffect(() => {
    if (tradingState.selectedStock && tradingState.quantity > 0) {
      console.log('🔄 useEffect triggering calculateOrderPreview')
      calculateOrderPreview()
    }
  }, [tradingState.selectedStock, tradingState.quantity, tradingState.orderType])

  const handleStockSelect = (symbol: string) => {
    selectStock(symbol)
  }

  const handleOrderSubmit = async () => {
    // Check if user is in guest mode
    if (isGuest || !user) {
      setShowSignInPrompt(true)
      return
    }

    try {
      const order = await executeOrder()
      if (order) {
        resetTradingState()
        clearSearch()
        onOrderExecuted?.()
      }
    } catch (err) {
      console.error('Order execution failed:', err)
    }
  }

  const canExecuteOrder = () => {
    console.log('🔍 Checking if order can be executed:', {
      selectedStock: tradingState.selectedStock,
      orderPreview: tradingState.orderPreview,
      orderType: tradingState.orderType,
      quantity: tradingState.quantity,
      submitting,
      loading
    })
    
    if (!tradingState.selectedStock || !tradingState.orderPreview) {
      console.log('❌ Cannot execute: missing stock or preview')
      return false
    }
    
    if (tradingState.orderType === 'buy') {
      const canBuy = tradingState.orderPreview.totalAmount <= tradingState.orderPreview.availableCash
      console.log('💰 Buy check:', {
        totalAmount: tradingState.orderPreview.totalAmount,
        availableCash: tradingState.orderPreview.availableCash,
        canBuy
      })
      return canBuy
    } else {
      const canSell = tradingState.quantity <= tradingState.orderPreview.availableShares
      console.log('📈 Sell check:', {
        quantity: tradingState.quantity,
        availableShares: tradingState.orderPreview.availableShares,
        canSell
      })
      return canSell
    }
  }

  return (
    <div className="space-y-6">
      {/* API Key Warning */}
      {!import.meta.env.VITE_FINNHUB_API_KEY && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg border bg-yellow-500/10 border-yellow-500/20"
        >
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-yellow-400" />
            <div>
              <span className="font-medium text-yellow-400">Demo Mode</span>
              <p className="text-sm text-yellow-300 mt-1">
                Using mock data. To get real-time stock data, add your Finnhub API key to .env.local
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Market Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-4 rounded-lg border ${
          marketHours.isOpen 
            ? 'bg-green-500/10 border-green-500/20' 
            : 'bg-yellow-500/10 border-yellow-500/20'
        }`}
      >
        <div className="flex items-center space-x-2">
          {marketHours.isOpen ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <Clock className="w-5 h-5 text-yellow-400" />
          )}
          <span className={`font-medium ${
            marketHours.isOpen ? 'text-green-400' : 'text-yellow-400'
          }`}>
            Market {marketHours.isOpen ? 'Open' : 'Closed'}
          </span>
          {!marketHours.isOpen && marketHours.nextOpen && (
            <span className="text-background-400 text-sm">
              • Opens {marketHours.nextOpen}
            </span>
          )}
        </div>
      </motion.div>

      {/* Stock Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-br from-background-900 to-background-800 rounded-2xl p-6 border border-background-700 shadow-xl"
      >
        <h3 className="text-lg font-semibold text-background-100 mb-4">Search Stocks</h3>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-background-400" />
          <input
            type="text"
            placeholder="Search by symbol or company name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-background-800 border border-background-600 rounded-lg text-background-100 placeholder-background-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {searchResults.map((stock) => (
              <button
                key={stock.symbol}
                onClick={() => handleStockSelect(stock.symbol)}
                className="w-full text-left p-3 hover:bg-background-700 rounded-lg transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-background-100">
                      {stock.symbol}
                    </div>
                    <div className="text-xs text-background-400">
                      {stock.name}
                    </div>
                  </div>
                  <div className="text-xs text-background-500">
                    {stock.exchange}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Selected Stock */}
        {selectedStock && (
          <div className="mt-4 p-4 bg-background-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-lg font-semibold text-background-100">
                  {selectedStock.symbol}
                </div>
                <div className="text-sm text-background-400">
                  {selectedStock.companyName}
                </div>
              </div>
              <button
                onClick={clearSearch}
                className="text-background-400 hover:text-background-200"
              >
                ×
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-xl font-bold text-background-100">
                ${selectedStock.currentPrice.toFixed(2)}
              </div>
              <div className={`flex items-center space-x-1 text-sm ${
                selectedStock.change >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {selectedStock.change >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span>
                  {selectedStock.change >= 0 ? '+' : ''}${selectedStock.change.toFixed(2)} ({selectedStock.changePercent.toFixed(2)}%)
                </span>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Order Form */}
      <AnimatePresence>
        {selectedStock && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-background-900/50 to-background-800/50 backdrop-blur-sm rounded-3xl p-6 border border-background-700/50 shadow-2xl"
          >
          <h3 className="text-lg font-semibold text-background-100 mb-6">Place Order</h3>

          {/* Order Type Toggle */}
          <div className="flex space-x-2 mb-8 bg-background-800/50 rounded-2xl p-1.5">
            <button
              onClick={() => setOrderType('buy')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 ${
                tradingState.orderType === 'buy'
                  ? 'bg-green-500 text-white shadow-lg'
                  : 'text-background-400 hover:text-background-200 hover:bg-background-700/50'
              }`}
            >
              Buy
            </button>
            <button
              onClick={() => setOrderType('sell')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 ${
                tradingState.orderType === 'sell'
                  ? 'bg-red-500 text-white shadow-lg'
                  : 'text-background-400 hover:text-background-200 hover:bg-background-700/50'
              }`}
            >
              Sell
            </button>
          </div>

          {/* Quantity Input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-background-300 mb-3">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={tradingState.quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="w-full px-4 py-4 bg-background-800/50 backdrop-blur-sm border border-background-600/50 rounded-2xl text-background-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
            />
          </div>

          {/* Order Preview */}
          {tradingState.orderPreview && (
            <div className="mb-6 p-4 bg-background-800 rounded-lg">
              <h4 className="text-sm font-medium text-background-300 mb-2">Order Preview</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-background-400">Total Amount:</span>
                  <span className="text-background-100 font-medium">
                    ${tradingState.orderPreview.totalAmount.toFixed(2)}
                  </span>
                </div>
                {tradingState.orderType === 'buy' && (
                  <div className="flex justify-between">
                    <span className="text-background-400">Available Cash:</span>
                    <span className="text-background-100">
                      ${tradingState.orderPreview.availableCash.toFixed(2)}
                    </span>
                  </div>
                )}
                {tradingState.orderType === 'sell' && (
                  <div className="flex justify-between">
                    <span className="text-background-400">Available Shares:</span>
                    <span className="text-background-100">
                      {tradingState.orderPreview.availableShares}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Debug Info */}
          <div className="mb-4 p-3 bg-background-800 rounded-lg text-xs">
            <div className="text-background-400 mb-2">Debug Info:</div>
            <div>Selected Stock: {tradingState.selectedStock ? 'Yes' : 'No'}</div>
            <div>Order Preview: {tradingState.orderPreview ? 'Yes' : 'No'}</div>
            <div>Can Execute: {canExecuteOrder() ? 'Yes' : 'No'}</div>
            <div>Submitting: {submitting ? 'Yes' : 'No'}</div>
            <div>Loading: {loading ? 'Yes' : 'No'}</div>
            {tradingState.orderPreview && (
              <div>
                <div>Available Cash: ${tradingState.orderPreview.availableCash.toFixed(2)}</div>
                <div>Total Amount: ${tradingState.orderPreview.totalAmount.toFixed(2)}</div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            onClick={handleOrderSubmit}
            disabled={!canExecuteOrder() || submitting || loading}
            whileHover={{ scale: canExecuteOrder() && !submitting && !loading ? 1.02 : 1 }}
            whileTap={{ scale: canExecuteOrder() && !submitting && !loading ? 0.98 : 1 }}
            className={`w-full py-4 px-6 rounded-2xl font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg ${
              tradingState.orderType === 'buy'
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            {submitting ? (
              <div className="flex items-center justify-center space-x-2">
                <Sparkles className="w-5 h-5 animate-spin" />
                <span>Executing...</span>
              </div>
            ) : (
              `${tradingState.orderType === 'buy' ? 'Buy' : 'Sell'} ${tradingState.quantity} shares`
            )}
          </motion.button>
        </motion.div>
        )}
      </AnimatePresence>

      {/* Sign In Prompt */}
      <SignInPrompt
        isOpen={showSignInPrompt}
        onClose={() => setShowSignInPrompt(false)}
        title="Sign in to execute trades"
        message="Create an account to start trading with virtual money and track your portfolio performance."
        action="Sign In to Trade"
      />
    </div>
  )
}

export default TradingPanel
